import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NigeriaHero from '../components/overview/NigeriaHero';
import DemocracySnapshot from '../components/overview/DemocracySnapshot';
import {
  Box,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import {
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Public as PublicIcon,
  HowToVote as ElectionsIcon,
  Event as EventIcon,
  Groups as VotersIcon,
  BarChart as TurnoutIcon,
} from '@mui/icons-material';
import DailyVote from '../components/DailyVote';
import DataService from '../services/dataService';
import { CHART_COLORS } from '../data/pocMockData';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PageTemplate, Section } from '../components/common/Page/PageTemplate';
import { StatsCard as CompactStatsCard } from '../components/common/Card/CardComponents';
import { LoadingState } from '../components/common/LoadingSkeleton';

const Overview: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [topCandidates, setTopCandidates] = useState<any[]>([]);
  const [socialStats, setSocialStats] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [insights, candidates, social] = await Promise.all([
          DataService.getElectionInsights(),
          Promise.resolve(DataService.getTopCandidates(5)),
          DataService.getSocialStatsAsync(),
        ]);
        setStats(insights);
        setTopCandidates(candidates);
        setSocialStats(social);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <PageTemplate title={t('pages.overview.title')} subtitle={t('pages.overview.subtitle')}>
        <LoadingState />
      </PageTemplate>
    );
  }

  // Extract short year from election string, e.g. "Presidential 2007" → "'07"
  const shortYear = (election: string): string => {
    const m = election?.match(/\d{4}/);
    return m ? `'${m[0].slice(2)}` : '';
  };

  // Use the surname (last whitespace-separated token) so two distinct people with
  // the same first name don't collapse to identical labels in the chart.
  const lastName = (name: string): string => {
    const parts = (name ?? '').trim().split(/\s+/);
    return parts.length > 0 ? parts[parts.length - 1] : '';
  };

  const chartLabel = (c: { name?: string; election?: string }): string =>
    `${lastName(c.name ?? '')} ${shortYear(c.election ?? '')}`.trim();

  // Deduplicate by the rendered chart label, not the raw (name, election) pair —
  // the underlying mock/DB rows for the same person sometimes differ slightly in
  // formatting ("Presidential 2007" vs "Presidential Election 2007") and would
  // otherwise both survive a raw-field dedup, showing as two identical-looking
  // "Yar'Adua '07" slices/legend entries. Preserves the first occurrence
  // (highest-vote entry since getTopCandidates sorts descending).
  const uniqueTopCandidates = topCandidates.filter(
    (c, i, arr) => arr.findIndex((x) => chartLabel(x) === chartLabel(c)) === i,
  );

  return (
    <PageTemplate
      title={t('pages.overview.title')}
      subtitle={t('pages.overview.subtitle')}
    >
      {/* Country hero */}
      <NigeriaHero />

      {/* Democracy health indicators */}
      <DemocracySnapshot />

      {/* Daily Vote */}
      <Box sx={{ mb: 3 }}>
        <DailyVote />
      </Box>

      {/* Vote Distribution & Candidate Comparison Charts */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
        gap: 3,
        mb: 6
      }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>{t('pages.overview.voteDistribution')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('pages.overview.percentageByCandidate')}</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={uniqueTopCandidates.map((c) => ({
                    name: chartLabel(c),
                    value: c.percentage || Math.round(((c.votes || 0) / (uniqueTopCandidates.reduce((s: number, x: any) => s + (x.votes || 0), 0) || 1)) * 100),
                  }))}
                  cx="50%" cy="50%" labelLine={false}
                  label={({ value }) => `${value}%`}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {uniqueTopCandidates.map((_, i) => (<Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                </Pie>
                {/* Full "Surname 'YY" labels live in the legend/tooltip, not on the
                    slice itself — with up to 5 slices in a 280px chart, printing
                    "name: value%" directly on each wedge produced overlapping,
                    illegible text. The short "%"-only label always fits. */}
                <Tooltip formatter={(value: number, _n, entry: any) => [`${value}%`, entry?.payload?.name]} />
                <Legend
                  wrapperStyle={{ fontSize: 11, lineHeight: '16px' }}
                  iconSize={8}
                  formatter={(value: string) => value}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>{t('pages.overview.candidateComparison')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('pages.overview.votesByCandidate')}</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={uniqueTopCandidates.map((c) => ({
                  name: chartLabel(c),
                  votes: (c.votes || 0) / 1000000,
                  fullName: c.name ?? '',
                }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.02)' }}
                  formatter={(value: number) => [`${value.toFixed(2)}M votes`, 'Votes']}
                />
                <Legend />
                <Bar dataKey="votes" radius={[4, 4, 0, 0]}>
                  {uniqueTopCandidates.map((candidate, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      name={chartLabel(candidate)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>
      {/* Stats Overview */}
      <Section>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}>
          <CompactStatsCard
            title="Total Elections"
            value={stats?.totalElections?.toLocaleString() || '12'}
            color="primary"
            icon={<ElectionsIcon />}
          />
          <CompactStatsCard
            title="Upcoming Elections"
            value={stats?.upcomingElections?.toLocaleString() ?? '—'}
            color="secondary"
            icon={<EventIcon />}
          />
          <CompactStatsCard
            title="Total Voters"
            value={stats?.totalVoters?.toLocaleString() ?? '—'}
            color="success"
            icon={<VotersIcon />}
          />
          <CompactStatsCard
            title="Average Turnout"
            value={`${stats?.averageTurnout ? Number(stats.averageTurnout).toFixed(1) : '68'}%`}
            color="info"
            icon={<TurnoutIcon />}
          />
        </Box>
      </Section>

      {/* Social Media Activity */}
      <Section>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}>
          {(() => {
            const ss = socialStats?.socialStats;
            if (!ss) return null;
            const platforms = Object.keys(ss).filter(k => typeof ss[k] === 'object' && ss[k]?.posts != null);
            const totalPosts = platforms.reduce((s, p) => s + (ss[p].posts || 0), 0);
            const totalReach = platforms.reduce((s, p) => s + (ss[p].reach || 0), 0);
            const totalImpressions = platforms.reduce((s, p) => s + (ss[p].impressions || 0), 0);
            const totalActiveUsers = platforms.reduce((s, p) => s + (ss[p].activeUsers || 0), 0);
            const engagementRate = totalImpressions > 0
              ? ((platforms.reduce((s, p) => s + (ss[p].engagement || 0), 0) / platforms.length)).toFixed(1)
              : '0';
            const fmt = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n);
            return (
              <>
                <CompactStatsCard title="Social Posts" value={fmt(totalPosts)} color="primary" icon={<TrendingIcon />} />
                <CompactStatsCard title="Engagement Rate" value={`${engagementRate}%`} color="success" icon={<TrendingIcon />} />
                <CompactStatsCard title="Active Users" value={fmt(totalActiveUsers)} color="info" icon={<PeopleIcon />} />
                <CompactStatsCard title="Reach" value={fmt(totalReach)} color="secondary" icon={<PublicIcon />} />
              </>
            );
          })()}
        </Box>
      </Section>
    </PageTemplate>
  );
};

export default Overview;
